/**
 * usePersonSearch — debounce + abort + result mapping for RAO people search.
 *
 * Port note: the legacy SPFx `PersonSearchBar` searched only on Enter / Search
 * button click. It has no typeahead / debounce because the jXRL CustSrch call
 * is expensive (parallel up to 3 fan-out + kill-list filter) and the Core
 * Director rate limits aggressive searching.
 *
 * For v3 we expose the hook in two modes:
 *
 *   1. Manual (`mode: 'manual'`, default) — match legacy behavior. Consumer
 *      calls `run(q)` explicitly (bound to Enter / click). Debounce + abort
 *      still apply — if the user presses Enter twice quickly the first
 *      request is aborted, preventing stale results from flashing.
 *
 *   2. Typeahead (`mode: 'typeahead'`) — fires CustSrch automatically
 *      `debounceMs` after the last keystroke, with prior in-flight requests
 *      aborted. Intended for a future typeahead dropdown; PeopleStep keeps
 *      manual mode for SPFx fidelity.
 *
 * The hook returns the same CustomerSearchRow[] shape the GlobalCustomerSearch
 * primitive parses, so either consumer can share presentation logic.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type { ApiClient } from '@retail-ops/api-client';
import { useApiClient } from '@/contexts/ApiClientContext';
import { unwrapJxrlArray, txt, hasJxrlError } from '@/utils/jxrl';
import type { CustomerSearchRow } from '@/components/primitives';

export type PersonSearchMode = 'manual' | 'typeahead';

export interface UsePersonSearchOptions {
  mode?: PersonSearchMode;
  /** Typeahead debounce (ms). Default 300. Ignored in manual mode. */
  debounceMs?: number;
  /** Max results per parallel CustSrch call. Default 25 — matches legacy. */
  maxResults?: number;
  /** Minimum query length to trigger a search. Default 2. */
  minLength?: number;
}

export interface UsePersonSearchResult {
  results: ReadonlyArray<CustomerSearchRow>;
  searching: boolean;
  error: string | null;
  /** Explicit trigger — use this in manual mode or on Enter. */
  run: (query: string) => Promise<void>;
  /** Reset results + error to initial state. */
  reset: () => void;
  /** Typeahead setter — debounced fire. No-op in manual mode. */
  onQueryChange: (query: string) => void;
}

// -----------------------------------------------------------------------------
// Parser — matches the GlobalCustomerSearch primitive (stay in sync if that
// primitive's parser changes).
// -----------------------------------------------------------------------------
function parseResponse(resp: unknown): CustomerSearchRow[] {
  if (hasJxrlError(resp)) return [];
  const rows = unwrapJxrlArray<Record<string, unknown>>(resp, 'CustSrchRecArray', 'CustSrchRec');
  const out: CustomerSearchRow[] = [];
  for (const c of rows) {
    // CustId arrives EITHER as a plain string OR as the masked-attribute
    // shape {"#text":"...","@MaskVal":"..."} — the same shape TaxId uses
    // right below. String() on the object form yields the literal
    // "[object Object]", which was ONE root cause behind THREE symptoms
    // (Bill + Collette, 2026-07-27): every row got the same cifId, so the
    // dedupe collapsed all matches to a single result; the tile rendered
    // "CIF [object Object]"; and the in-cart index keyed on that literal,
    // so once ANY person was in the cart EVERY search result showed
    // "In Cart". Use txt() exactly as TaxId does.
    const cifId = txt(c.CustId);
    if (!cifId) continue;
    const pn = (c.PersonName as Record<string, unknown> | undefined) ?? {};
    const firstName = typeof pn.FirstName === 'string' ? pn.FirstName : '';
    const lastName = typeof pn.LastName === 'string' ? pn.LastName : '';
    const comName = typeof pn.ComName === 'string' ? pn.ComName : '';
    const middleName = typeof pn.MiddleName === 'string' ? pn.MiddleName : '';
    const taxId = txt(c.TaxId);
    const addrObj = (c.Addr as Record<string, unknown> | undefined) ?? {};
    const street = typeof addrObj.StreetAddr1 === 'string' ? addrObj.StreetAddr1 : '';
    const city = typeof addrObj.City === 'string' ? addrObj.City : '';
    const stCode = typeof addrObj.StateCode === 'string' ? addrObj.StateCode : '';
    const address = street ? `${street}, ${city} ${stCode}`.trim() : '';
    const custTypeCode = typeof c.CustType === 'string' ? c.CustType : '';
    const custType: CustomerSearchRow['custType'] =
      custTypeCode === 'I' ? 'Individual' : custTypeCode === 'C' ? 'Business' : custTypeCode;
    // BirthDt may arrive on the CustSrch record (same field parseCustInq reads
    // at CustDetail.BirthDt) — extract it when present so the no-dup-CIF
    // name+DOB trigger can tighten matches (Bill 2026-06-12, answer #2). Core
    // Director omits it on some CustSrch projections; absent → name-only.
    const dob = txt(c.BirthDt) || txt(c.DOB) || '';
    out.push({
      cifId,
      middleName,
      name: comName || `${firstName} ${lastName}`.trim(),
      firstName,
      lastName,
      taxId,
      phone: typeof c.PhoneNum === 'string' ? c.PhoneNum : '',
      address,
      ...(dob ? { dob } : {}),
      custType,
    });
  }
  return out;
}

function splitFullName(q: string): { firstName: string; lastName: string; middleName: string } {
  const parts = q.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return { firstName: '', lastName: '', middleName: '' };
  if (parts.length === 1) return { firstName: parts[0], lastName: '', middleName: '' };
  if (parts.length === 2) return { firstName: parts[0], lastName: parts[1], middleName: '' };
  // 3+ tokens: FIRST is the first token, LAST is the LAST token, everything
  // between is the middle name. The old code did lastName = parts.slice(1),
  // so "John K Wiggins" searched LastName="K Wiggins" and matched nobody —
  // adding a middle initial made a search FAIL that worked without it
  // (Bill + Collette, 2026-07-27).
  //
  // The middle name is deliberately NOT sent to Core. Core stores the FULL
  // middle name ("Kent"), so querying MiddleName="K" would miss the very
  // person being searched for. It is used below to rank/narrow the results
  // client-side by PREFIX, which matches K -> Kent and E -> E alike.
  return {
    firstName: parts[0],
    lastName: parts[parts.length - 1],
    middleName: parts.slice(1, -1).join(' '),
  };
}

/**
 * Narrow results by a typed middle name/initial WITHOUT hiding real matches.
 * Prefix-matches against the row's middle name; if nothing matches (e.g. Core
 * has no middle name recorded) the full set is returned unchanged, so typing a
 * middle initial can never turn a working search into "no results".
 */
function narrowByMiddleName<T extends { middleName?: string }>(rows: T[], middleName: string): T[] {
  const m = middleName.trim().toLowerCase().replace(/\.$/, '');
  if (!m) return rows;
  const hits = rows.filter((r) => (r.middleName ?? '').trim().toLowerCase().startsWith(m));
  return hits.length > 0 ? hits : rows;
}

export interface RunCustomerSearchOptions {
  /** Max results per parallel CustSrch call. Default 25 — matches legacy. */
  maxResults?: number;
  /** Minimum query length to trigger a search. Default 2. */
  minLength?: number;
  /** Abort signal — forwarded to every fan-out request. */
  signal?: AbortSignal;
}

/**
 * Standalone CustSrch fan-out + parse + dedupe + sort. Returns the merged
 * CustomerSearchRow[]. Single source of the RAO customer-search wire shape —
 * both the interactive `usePersonSearch` hook AND the silent no-dup-CIF guard
 * in PeopleStep call this so the two paths can never drift (SSN→TaxId,
 * full-name→PersonName, single-token→LastName+FirstName fan-out, CIF→CustId).
 * Errors on individual fan-out calls degrade to an empty result for that call
 * (never throws), so a partial Core Director outage still returns what matched.
 */
export async function runCustomerSearch(
  client: Pick<ApiClient, 'jxrl'>,
  query: string,
  opts: RunCustomerSearchOptions = {},
): Promise<CustomerSearchRow[]> {
  const { maxResults = 25, minLength = 2, signal } = opts;
  const q = query.trim();
  if (!q || q.length < minLength) return [];

  const countQs = `Count=${maxResults}`;
  const isSsnLike = /^\d{3}-?\d{2}-?\d{4}$/.test(q) || /^\d{9}$/.test(q);
  const isCifLike = /^\d{6,}$/.test(q);

  // Fix A (Bill 2026-06-03): CustSrch is an idempotent read — opt into the
  // http.ts jXRL cold-start 5xx retry (retry:true) so the first search against a
  // cold Function instance self-heals instead of surfacing a 502. Reused by both
  // the interactive search and the silent no-dup-CIF guard.
  const post = (body: Record<string, unknown>): Promise<unknown> =>
    client.jxrl
      .post('Customer/CustSrch', body, { queryParams: countQs, retry: true, ...(signal ? { signal } : {}) })
      .catch(() => ({ Details: [{ Code: 'ERR' }] }));

  const requests: Array<Promise<unknown>> = [];
  if (isSsnLike) {
    requests.push(post({ TaxId: q.replace(/\D/g, '') }));
  } else {
    const { firstName, lastName } = splitFullName(q);
    if (firstName && lastName) {
      requests.push(post({ PersonName: { FirstName: firstName, LastName: lastName } }));
    } else if (firstName) {
      requests.push(post({ PersonName: { LastName: firstName } }));
      requests.push(post({ PersonName: { FirstName: firstName } }));
    }
    if (isCifLike) {
      requests.push(post({ CustId: q }));
    }
  }

  const settled = await Promise.all(requests);
  if (signal?.aborted) return [];

  const seen = new Set<string>();
  const merged: CustomerSearchRow[] = [];
  for (const resp of settled) {
    for (const row of parseResponse(resp)) {
      if (seen.has(row.cifId)) continue;
      seen.add(row.cifId);
      merged.push(row);
    }
  }
  // Narrow by a typed middle name/initial (prefix, never hides real matches).
  const narrowed = isSsnLike || isCifLike ? merged : narrowByMiddleName(merged, splitFullName(q).middleName);
  narrowed.sort((a, b) => {
    const lastCmp = (a.lastName || a.name).localeCompare(b.lastName || b.name);
    if (lastCmp !== 0) return lastCmp;
    return (a.firstName || '').localeCompare(b.firstName || '');
  });
  return narrowed;
}

/**
 * Normalize a DOB to bare digits for a tolerant compare (handles YYYY-MM-DD,
 * MM/DD/YYYY, and stray separators). Returns '' when there's nothing to compare.
 */
function dobDigits(dob: string | undefined): string {
  return (dob ?? '').replace(/\D/g, '');
}

/**
 * Name+DOB duplicate filter (Bill 2026-06-12, answer #2). Given the candidate
 * rows from a NAME-based `runCustomerSearch`, keep only rows whose DOB matches
 * the entered draft's DOB. Rows whose CustSrch projection omitted BirthDt are
 * KEPT (we can't disprove the match here — the compare modal hydrates the full
 * on-file DOB so the FSR confirms visually). When the draft itself has no DOB,
 * this is a no-op (returns the input rows) so behaviour falls back to name-only.
 *
 * This TIGHTENS the prior full-name-only fallback (PeopleStep.tsx:572-577):
 * a different person who merely shares a name but has a known, different DOB no
 * longer trips the prompt.
 */
export function filterByNameAndDob(
  rows: ReadonlyArray<CustomerSearchRow>,
  draftDob: string | undefined,
): CustomerSearchRow[] {
  const want = dobDigits(draftDob);
  if (!want) return [...rows];
  return rows.filter((r) => {
    const have = dobDigits(r.dob);
    return have === '' || have === want; // unknown DOB → keep; known → must match
  });
}

export function usePersonSearch(opts: UsePersonSearchOptions = {}): UsePersonSearchResult {
  const { mode = 'manual', debounceMs = 300, maxResults = 25, minLength = 2 } = opts;
  const client = useApiClient();
  const [results, setResults] = useState<ReadonlyArray<CustomerSearchRow>>([]);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // AbortController for in-flight requests — aborted when a new call starts
  // or the consumer unmounts.
  const controllerRef = useRef<AbortController | null>(null);
  // Debounce timer for typeahead mode.
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      controllerRef.current?.abort();
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const reset = useCallback((): void => {
    setResults([]);
    setError(null);
    setSearching(false);
  }, []);

  const run = useCallback(
    async (query: string): Promise<void> => {
      const q = query.trim();
      if (!q || q.length < minLength) {
        reset();
        return;
      }
      // Abort prior in-flight request so stale results can't overwrite fresh ones.
      controllerRef.current?.abort();
      const controller = new AbortController();
      controllerRef.current = controller;

      setSearching(true);
      setError(null);

      try {
        const merged = await runCustomerSearch(client, q, {
          maxResults,
          minLength,
          signal: controller.signal,
        });
        if (controller.signal.aborted) return;
        setResults(merged);
      } catch (err) {
        if (controller.signal.aborted) return;
        setError(err instanceof Error ? err.message : 'Search failed');
        setResults([]);
      } finally {
        if (!controller.signal.aborted) setSearching(false);
      }
    },
    [client, maxResults, minLength, reset],
  );

  const onQueryChange = useCallback(
    (query: string): void => {
      if (mode !== 'typeahead') return;
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        void run(query);
      }, debounceMs);
    },
    [debounceMs, mode, run],
  );

  return { results, searching, error, run, reset, onQueryChange };
}
